/**
 * useThreadReactions — the host's view of reactions on the open thread
 * (PR-4, 2026-09-09): the store's map (from the thread store's listener)
 * with an OPTIMISTIC overlay for the viewer's own toggles — a tap shows at
 * once, the server echo replaces it, a refusal rolls it back.
 */
import { useCallback, useMemo, useRef, useState } from 'react';
import type { ChatMessage } from './types';
import {
  reactionTarget,
  summarizeReactions,
  toggleInList,
  type ReactionsByMessage,
  type ReactionSummaryEntry,
} from '../../services/messageReactions/reactionModel';
import { toggleReaction, type ToggleReactionRefusal } from '../../repositories/messageReactionsRepository';
import { messageReactionsEnabled } from '../../services/messageReactions/featureFlags';

export interface UseThreadReactionsArgs {
  threadId: string | null | undefined;
  selfUid: string | null | undefined;
  accountId?: string | null;
  isGroup: boolean;
  /** From the thread store's listener. */
  reactions: ReactionsByMessage;
  /** From the thread store: shared id → the store's orderTs (delivery proof in a group thread). */
  sharedOrderTs: Readonly<Record<string, number>>;
  enabled?: boolean;
  /**
   * Register the thread (its membership row) and resolve true when it now has
   * one — called ONCE when a solo thread's write is permission-denied, which
   * is what a thread that never had a turn since registration shipped gets.
   */
  registerThread?: () => Promise<boolean>;
  /** A write the store refused after the retry — the host says so (D9 failure copy). */
  onRefused?: (reason: ToggleReactionRefusal) => void;
}

export interface ThreadReactionsApi {
  enabled: boolean;
  /** The reaction doc id for a message, or null when it cannot take one (yet). */
  targetFor: (m: ChatMessage) => string | null;
  /** Chips for a message — server state with the viewer's optimistic toggles applied. */
  summaryFor: (m: ChatMessage) => ReactionSummaryEntry[];
  /** The viewer's own emoji on a message (optimistic). */
  mineFor: (m: ChatMessage) => string[];
  /** Toggle the viewer's reaction; resolves false when refused or not reactable. */
  toggle: (m: ChatMessage, emoji: string) => Promise<boolean>;
}

/** Optimistic layer: messageId → the viewer's own list as they see it. */
type OwnOverlay = Record<string, string[]>;
const EMPTY_REACTIONS: ReactionsByMessage = {};
const EMPTY_ORDER_TS: Readonly<Record<string, number>> = {};

export function useThreadReactions(args: UseThreadReactionsArgs): ThreadReactionsApi {
  const enabled = (args.enabled ?? true) && messageReactionsEnabled();
  const self = (args.selfUid ?? '').trim();
  const [overlay, setOverlay] = useState<OwnOverlay>({});
  const overlayThreadRef = useRef<string | null>(null);
  // A thread switch drops the overlay (stale optimism must never paint the next thread).
  if (overlayThreadRef.current !== (args.threadId ?? null)) {
    overlayThreadRef.current = args.threadId ?? null;
    if (Object.keys(overlay).length) setOverlay({});
  }

  // The store's maps may be absent (a mocked store, an early mount) — never a crash.
  const reactions = args.reactions ?? EMPTY_REACTIONS;
  const sharedOrderTs = args.sharedOrderTs ?? EMPTY_ORDER_TS;
  const ctx = useMemo(() => ({ selfUid: self, isGroup: args.isGroup, sharedOrderTs }), [self, args.isGroup, sharedOrderTs]);

  const targetFor = useCallback((m: ChatMessage): string | null => {
    if (!enabled) return null;
    return reactionTarget(m, ctx)?.messageId ?? null;
  }, [enabled, ctx]);

  const effectiveByUser = useCallback((messageId: string) => {
    const server = reactions[messageId]?.byUser ?? {};
    const own = overlay[messageId];
    if (!self || own === undefined) return server;
    return { ...server, [self]: own };
  }, [reactions, overlay, self]);

  const summaryFor = useCallback((m: ChatMessage): ReactionSummaryEntry[] => {
    const id = reactionTarget(m, { selfUid: self, isGroup: false, sharedOrderTs: ctx.sharedOrderTs })?.messageId
      ?? (typeof m.sharedMessageId === 'string' ? m.sharedMessageId : null)
      ?? null;
    if (!id) return [];
    return summarizeReactions(effectiveByUser(id), self);
  }, [self, ctx.sharedOrderTs, effectiveByUser]);

  const mineFor = useCallback((m: ChatMessage): string[] => {
    const id = targetFor(m);
    if (!id || !self) return [];
    const list = effectiveByUser(id)[self];
    return Array.isArray(list) ? list : [];
  }, [targetFor, self, effectiveByUser]);

  const toggle = useCallback(async (m: ChatMessage, emoji: string): Promise<boolean> => {
    if (!enabled || !self || !args.threadId) return false;
    const target = reactionTarget(m, ctx);
    if (!target) return false;
    const current = effectiveByUser(target.messageId)[self];
    const next = toggleInList(current, emoji);
    if (!next) return false;
    const previous = current;
    setOverlay((prev) => ({ ...prev, [target.messageId]: next.next }));
    const payload = {
      threadId: args.threadId,
      messageId: target.messageId,
      orderTs: target.orderTs,
      uid: self,
      emoji,
      add: next.added,
      accountId: args.accountId ?? null,
    };
    let result = await toggleReaction(payload);
    if (!result.ok && result.reason === 'permission-denied' && !args.isGroup && args.registerThread) {
      // A solo thread the rules do not know yet: register it once, then retry.
      const registered = await args.registerThread().catch(() => false);
      if (registered) result = await toggleReaction(payload);
    }
    if (!result.ok) {
      // Roll back to what the server said (or to nothing when it had nothing).
      setOverlay((prev) => {
        const out = { ...prev };
        if (Array.isArray(previous)) out[target.messageId] = previous;
        else delete out[target.messageId];
        return out;
      });
      if (result.reason === 'permission-denied' || result.reason === 'error') args.onRefused?.(result.reason);
    }
    return result.ok;
  }, [enabled, self, args.threadId, args.accountId, args.isGroup, args.registerThread, args.onRefused, ctx, effectiveByUser]);

  return useMemo(() => ({ enabled, targetFor, summaryFor, mineFor, toggle }), [enabled, targetFor, summaryFor, mineFor, toggle]);
}
